import React, { useCallback, useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material"
import {
  AddOutlined,
  ChevronRightOutlined,
  InfoOutlined,
  StarOutlined,
  StarRounded,
} from "@mui/icons-material"
import { money, sectionLabel } from "./utils"
import { itemTypesWithIcons } from "constants/unity"

// Los `type` del desglose de scrap-cype llegan como el mismo literal que usa
// `itemTypesWithIcons` ("Mano de obra", "Materiales", "Maquinaria y medios auxiliares",
// "Sin clasificar"), así que se reutilizan esos iconos y el desglose se ve igual que en el
// modal de partida. Un tipo desconocido cae en el label de texto, no en un icono erróneo.
const TYPE_ICONS = new Map(
  itemTypesWithIcons.map(({ type, icon }) => [type, icon]),
)

const DIVIDER = "rgba(0,0,0,0.12)"
const MUTED = "rgba(0,0,0,0.6)"
const YELLOW = "#ffd52b"

// Label encima y valor debajo, ambos al mismo borde izquierdo: en un panel de ~420 px el
// patrón label-izquierda/valor-derecha parte los valores largos ("CYPE generador de
// precios", el breadcrumb de ubicación) y deja cada fila con una sangría distinta.
const DataRow = ({ label, value }) => (
  <Box>
    <Typography sx={{ color: MUTED, fontWeight: 600, fontSize: 12 }}>
      {label}
    </Typography>
    <Typography
      sx={{ color: "#222", fontWeight: 700, fontSize: 14, lineHeight: 1.45 }}
    >
      {value}
    </Typography>
  </Box>
)
DataRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
}

const FieldGroup = ({ children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
    {children}
  </Box>
)
FieldGroup.propTypes = { children: PropTypes.node.isRequired }

const TabSummary = ({ item }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
    <FieldGroup>
      <DataRow label={i18n.t("partBrowser.detail.code")} value={item.code} />
      <DataRow
        label={i18n.t("partBrowser.detail.unitPrice")}
        value={`${money(item.unitPrice)} / ${item.unit}`}
      />
      <DataRow label={i18n.t("partBrowser.detail.unit")} value={item.unit} />
      <DataRow
        label={i18n.t("partBrowser.detail.source")}
        value={item.source}
      />
      <DataRow
        label={i18n.t("partBrowser.detail.breakdown")}
        value={
          item.hasBreakdown
            ? i18n.t("partBrowser.detail.yes")
            : i18n.t("partBrowser.detail.no")
        }
      />
    </FieldGroup>

    {/* OCULTO — pendiente de backend: `targetCost`, `clientPrice` y `margin` no existen
        en la respuesta de scrap-cype. `mapItem` (useCypeSearch.js) los inicializa a null,
        así que "Margen" renderiza literalmente "null%".
        Para reactivar: estos datos son de negocio (coste objetivo / precio cliente por
        organización), van en el backend de planhopper, no en el catálogo CYPE. */}
    <Box sx={{ display: "none" }}>
      <FieldGroup>
        <DataRow label="Coste objetivo" value={money(item.targetCost)} />
        <DataRow label="Precio cliente" value={money(item.clientPrice)} />
        <DataRow label="Margen" value={`${item.margin}%`} />
      </FieldGroup>
    </Box>

    <Box sx={{ borderTop: `1px solid ${DIVIDER}`, pt: 2.5 }}>
      <FieldGroup>
        <DataRow
          label={i18n.t("partBrowser.detail.location")}
          value={item.chapterPath?.length ? item.chapterPath.join(" › ") : "—"}
        />
        <DataRow
          label={i18n.t("partBrowser.detail.section")}
          value={sectionLabel(item.section) || "—"}
        />
        {/* OCULTO — pendiente de backend:
            - "Provincia": scrap-cype no indexa provincia (el catálogo CYPE no está
              regionalizado en los BC3 que se scrapean); `mapItem` no la rellena.
            - "Actualizada": `updatedAt` es el timestamp de indexación, no la fecha real
              del BC3 (followup 05 en scrap-cype).
            - "Usos": `usageCount` está fijado a 0 en `mapItem`; requiere telemetría de
              uso en el backend de planhopper. */}
        <Box sx={{ display: "none", flexDirection: "column", gap: 2.25 }}>
          <DataRow label="Provincia" value={item.province} />
          <DataRow
            label="Actualizada"
            value={
              item.source === "CYPE generador de precios" ? item.updatedAt : "—"
            }
          />
          <DataRow
            label="Usos"
            value={`${item.usageCount || 0} presupuestos`}
          />
        </Box>
      </FieldGroup>
    </Box>

    {item.originBudgetName && (
      <Box sx={{ borderTop: `1px solid ${DIVIDER}`, pt: 2.5 }}>
        <Typography sx={{ color: MUTED, fontWeight: 600, fontSize: 12 }}>
          {i18n.t("partBrowser.detail.internalOrigin")}
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
          {item.originBudgetName}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#4f4f4f" }}>
          {item.originChapter || i18n.t("partBrowser.detail.noChapter")} ·{" "}
          {i18n.t("partBrowser.detail.usedIn", { count: item.usageCount })}
        </Typography>
      </Box>
    )}
  </Box>
)
TabSummary.propTypes = { item: PropTypes.object.isRequired }

const TabBreakdown = ({ item }) => {
  const total = item.breakdown.reduce((acc, line) => acc + line.cost, 0)
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.25,
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
          {i18n.t("partBrowser.detail.breakdownTitle")}
        </Typography>
        <Chip
          label={money(total)}
          variant="outlined"
          size="small"
          sx={{ fontWeight: 800 }}
        />
      </Box>
      <Box>
        {item.breakdown.length ? (
          item.breakdown.map((line, i) => (
            <Box
              key={line.code || `${line.name}-${i}`}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                py: 1.125,
                borderBottom:
                  i === item.breakdown.length - 1
                    ? "none"
                    : `1px solid ${DIVIDER}`,
              }}
            >
              {TYPE_ICONS.has(line.type) ? (
                <Tooltip title={line.type} placement="top" arrow>
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 28,
                      display: "flex",
                      mt: 0.25,
                      "& svg": { width: 18, height: 18 },
                    }}
                  >
                    {TYPE_ICONS.get(line.type)}
                  </Box>
                </Tooltip>
              ) : (
                <Typography
                  sx={{
                    flexShrink: 0,
                    width: 28,
                    fontSize: 11,
                    color: MUTED,
                    fontWeight: 700,
                  }}
                >
                  {line.type}
                </Typography>
              )}
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  pr: 1,
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                {line.name}
              </Typography>
              <Typography
                sx={{
                  flexShrink: 0,
                  width: 56,
                  textAlign: "right",
                  fontSize: 14,
                }}
              >
                {line.quantity}
              </Typography>
              <Typography
                sx={{
                  flexShrink: 0,
                  width: 40,
                  textAlign: "right",
                  fontSize: 14,
                }}
              >
                {line.unit}
              </Typography>
              <Typography
                sx={{
                  flexShrink: 0,
                  width: 80,
                  textAlign: "right",
                  fontSize: 14,
                  fontWeight: 800,
                }}
              >
                {money(line.cost)}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography sx={{ fontSize: 14, color: MUTED }}>
            {i18n.t("partBrowser.detail.noBreakdown")}
          </Typography>
        )}
      </Box>
    </Box>
  )
}
TabBreakdown.propTypes = { item: PropTypes.object.isRequired }

// `hidden` marca pestañas cuyo contenido aún no tiene datos reales en el backend.
// Se mantienen aquí (y su render más abajo) para reactivarlas cambiando sólo el flag.
const TAB_LABELS = [
  { value: "summary", label: i18n.t("partBrowser.detail.tabSummary") },
  { value: "breakdown", label: i18n.t("partBrowser.detail.tabBreakdown") },
  { value: "description", label: i18n.t("partBrowser.detail.tabDescription") },
  // OCULTO — pendiente de backend: el contenido sólo muestra `usageCount`, que está
  // fijado a 0 en `mapItem` (useCypeSearch.js). No hay histórico de uso en scrap-cype;
  // requiere telemetría en el backend de planhopper.
  {
    value: "history",
    label: i18n.t("partBrowser.detail.tabHistory"),
    hidden: true,
  },
]

const MIN_WIDTH = 420
const COLLAPSED_WIDTH = 56

const PartBrowserDetail = ({
  selected,
  canAdd,
  addedIds,
  addingId,
  bankSavedIds,
  tab,
  onTabChange,
  onPrimaryAction,
  onSaveToBank,
  onToggleFavorite,
  onHide,
  collapsed,
  onShow,
}) => {
  const [panelWidth, setPanelWidth] = useState(MIN_WIDTH)
  const isAdding = !!selected && addingId === selected.id
  const isDragging = useRef(false)
  const dragStartX = useRef(0)
  const dragStartWidth = useRef(MIN_WIDTH)

  const handleMouseDown = useCallback(
    e => {
      e.preventDefault()
      isDragging.current = true
      dragStartX.current = e.clientX
      dragStartWidth.current = panelWidth
      document.body.style.cursor = "ew-resize"
      document.body.style.userSelect = "none"
    },
    [panelWidth],
  )

  useEffect(() => {
    const onMouseMove = e => {
      if (!isDragging.current) return
      const delta = dragStartX.current - e.clientX
      const maxWidth = Math.floor(window.innerWidth / 2)
      const next = Math.min(
        maxWidth,
        Math.max(MIN_WIDTH, dragStartWidth.current + delta),
      )
      setPanelWidth(next)
    }
    const onMouseUp = () => {
      if (!isDragging.current) return
      isDragging.current = false
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
    }
    window.addEventListener("mousemove", onMouseMove)
    window.addEventListener("mouseup", onMouseUp)
    return () => {
      window.removeEventListener("mousemove", onMouseMove)
      window.removeEventListener("mouseup", onMouseUp)
    }
  }, [])

  return (
    <Box
      sx={{
        width: collapsed ? COLLAPSED_WIDTH : panelWidth,
        flexShrink: 0,
        borderLeft: `1px solid ${DIVIDER}`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: isDragging.current ? "none" : "width 220ms ease",
        position: "relative",
      }}
    >
      {/* Resize handle — only shown when expanded */}
      {!collapsed && (
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 6,
            zIndex: 10,
            cursor: "ew-resize",
            "&:hover": {
              "&::after": {
                opacity: 1,
              },
            },
            "&::after": {
              content: '""',
              position: "absolute",
              left: 2,
              top: 0,
              bottom: 0,
              width: 2,
              background: "#b0b0b0",
              opacity: 0,
              transition: "opacity 150ms ease",
              borderRadius: 1,
            },
          }}
        />
      )}
      {/* Collapsed strip */}
      <Box
        sx={{
          position: "absolute",
          width: 56,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 1.5,
          opacity: collapsed ? 1 : 0,
          pointerEvents: collapsed ? "auto" : "none",
          transition: "opacity 150ms ease",
        }}
      >
        <IconButton
          title={i18n.t("partBrowser.detail.showDetail")}
          onClick={onShow}
          sx={{ border: `1px solid ${DIVIDER}`, borderRadius: 1.25 }}
        >
          <InfoOutlined sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      {/* Full content — fades out when collapsed */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          opacity: collapsed ? 0 : 1,
          pointerEvents: collapsed ? "none" : "auto",
          transition: "opacity 150ms ease",
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2.75, py: 2, borderBottom: `1px solid ${DIVIDER}` }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {selected ? (
                <Typography
                  sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.25 }}
                >
                  {selected.shortDescription}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 14, color: MUTED }}>
                  {i18n.t("partBrowser.detail.selectPrompt")}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={onHide}
              title={i18n.t("partBrowser.detail.hideDetail")}
              sx={{
                border: `1px solid ${DIVIDER}`,
                borderRadius: 1.25,
                flexShrink: 0,
              }}
            >
              <ChevronRightOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          {selected && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
              {addedIds.includes(selected.id) && (
                <Chip
                  label={i18n.t("partBrowser.detail.added")}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,211,41,0.65)",
                    color: "#222",
                    fontWeight: 800,
                    fontSize: 11,
                  }}
                />
              )}
              {(() => {
                const [d, m, y] = (selected.updatedAt || "").split("/")
                const updated = new Date(y, m - 1, d)
                const stale =
                  y && Date.now() - updated.getTime() > 90 * 24 * 60 * 60 * 1000
                return stale ? (
                  // OCULTO — pendiente de backend: doble motivo. (1) `onClick` está vacío,
                  // no hay endpoint para refrescar una partida. (2) La detección de obsoleta
                  // se basa en `updatedAt`, que es el timestamp de indexación y no la fecha
                  // real del BC3 (followup 05), así que el badge saltaría por un dato falso.
                  <Chip
                    label="Actualizar"
                    size="small"
                    variant="outlined"
                    onClick={() => {}}
                    sx={{ display: "none", fontSize: 11, cursor: "pointer" }}
                  />
                ) : null
              })()}
            </Box>
          )}
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => onTabChange(v)}
          sx={{
            borderBottom: `1px solid ${DIVIDER}`,
            minHeight: 40,
            "& .MuiTab-root": {
              minHeight: 40,
              fontSize: 13,
              fontWeight: 600,
              color: MUTED,
              flex: 1,
            },
            "& .Mui-selected": { color: "#222 !important", fontWeight: 800 },
            "& .MuiTabs-indicator": { backgroundColor: "#222" },
          }}
        >
          {TAB_LABELS.filter(({ hidden }) => !hidden).map(
            ({ value, label }) => (
              <Tab key={value} value={value} label={label} />
            ),
          )}
        </Tabs>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", px: 2.75, py: 2.5 }}>
          {selected ? (
            <>
              {tab === "summary" && <TabSummary item={selected} />}
              {tab === "breakdown" && <TabBreakdown item={selected} />}
              {tab === "description" && (
                <Typography
                  sx={{ fontSize: 14, color: "#222", lineHeight: 1.6 }}
                >
                  {selected.longDescription}
                </Typography>
              )}
              {tab === "history" && (
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 14, mb: 1 }}>
                    {i18n.t("partBrowser.detail.tabHistory")}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#4f4f4f" }}>
                    {i18n.t("partBrowser.detail.usedInDot", {
                      count: selected.usageCount || 0,
                    })}
                  </Typography>
                </Box>
              )}
            </>
          ) : null}
        </Box>

        {/* Footer actions */}
        {canAdd && (
          <Box sx={{ px: 2.75, py: 2, borderTop: `1px solid ${DIVIDER}` }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Button
                fullWidth
                variant={
                  selected && addedIds.includes(selected.id)
                    ? "outlined"
                    : "contained"
                }
                color={
                  selected && addedIds.includes(selected.id)
                    ? "inherit"
                    : "branded"
                }
                startIcon={
                  isAdding ? (
                    <CircularProgress size={16} sx={{ color: "#222" }} />
                  ) : !(selected && addedIds.includes(selected.id)) ? (
                    <AddOutlined />
                  ) : null
                }
                disabled={!selected || !!addingId}
                onClick={() => selected && onPrimaryAction(selected)}
                sx={{ fontWeight: 700, borderColor: "rgba(34,34,34,0.35)" }}
              >
                {isAdding
                  ? i18n.t("partBrowser.detail.adding")
                  : selected && addedIds.includes(selected.id)
                    ? i18n.t("partBrowser.detail.added")
                    : i18n.t("partBrowser.detail.add")}
              </Button>
              <Box sx={{ display: "none", gap: 1, alignItems: "center" }}>
                {/* OCULTO — pendiente de backend: `handleSaveToBank` sólo hace setState de
                `bankSavedIds`; no persiste y se pierde al refrescar. Requiere endpoint de
                banco de precios en el backend de planhopper (con auth), no en scrap-cype. */}
                <Button
                  variant="outlined"
                  disabled={!selected}
                  onClick={() => selected && onSaveToBank(selected)}
                  sx={{
                    display: "none",
                    flex: 1,
                    fontWeight: 700,
                    color: "#222",
                    borderColor: "rgba(34,34,34,0.35)",
                    fontSize: 13,
                  }}
                >
                  {selected && bankSavedIds.includes(selected.id)
                    ? "En banco"
                    : "Guardar en banco"}
                </Button>
                {/* OCULTO — pendiente de backend: los favoritos no están implementados.
                `toggleFavorite` (PartBrowser.js) sólo hace setState de `favorites`: no
                persiste en ningún sitio y se pierde al refrescar, así que marcar una
                favorita no sirve para nada. Se oculta el ⭐ completo, no sólo el filtro.
                Requiere endpoint de favoritos en el backend de planhopper (con auth),
                no en scrap-cype (catálogo de referencia read-only y cacheado público). */}
                <IconButton
                  disabled={!selected}
                  title={
                    selected?.favorite
                      ? "Quitar de favoritas"
                      : "Guardar en favoritas"
                  }
                  onClick={() => selected && onToggleFavorite(selected.id)}
                  sx={{
                    display: "none",
                    border: `1px solid ${DIVIDER}`,
                    borderRadius: 1.25,
                  }}
                >
                  {selected?.favorite ? (
                    <StarRounded sx={{ fontSize: 18, color: "#b58a00" }} />
                  ) : (
                    <StarOutlined sx={{ fontSize: 18, color: MUTED }} />
                  )}
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}
      </Box>{" "}
      {/* end full-content Box */}
    </Box>
  )
}

PartBrowserDetail.propTypes = {
  selected: PropTypes.object,
  canAdd: PropTypes.bool,
  addedIds: PropTypes.array.isRequired,
  addingId: PropTypes.string,
  bankSavedIds: PropTypes.array.isRequired,
  tab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onPrimaryAction: PropTypes.func.isRequired,
  onSaveToBank: PropTypes.func.isRequired,
  onToggleFavorite: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
  collapsed: PropTypes.bool,
  onShow: PropTypes.func,
}
PartBrowserDetail.defaultProps = {
  selected: null,
  canAdd: false,
  addingId: null,
  collapsed: false,
  onShow: null,
}

export default PartBrowserDetail
